'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { FileTextIcon, MountainIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import NextImage from 'next/image';
import { useEffect, useState } from 'react';

import { ClassIconImg } from '@/app/editor/components/ui/ClassIconImg';
import { useSceneScreenshot } from '@/app/editor/hooks/useSceneScreenshot';
import { ASSET_ICONS, AssetType, getAssetType } from '@/editor/core/assetTypes';
import { assetsStore, editorStore } from '@/editor/store';
import { type IAssetFile } from '@/editor/store/models';

export interface AssetThumbnailProps {
  asset: IAssetFile;
  iconSize: number;
  selected?: boolean;
}



// ASSET_ICONS is now imported from @/editor/core/assetTypes

/** Hook to efficiently load thumbnail URL for supported assets with preloading */
const useThumbnailUrl = (asset: IAssetFile, isThumbnailServiceReady: boolean): { thumbnailUrl: string | null; isLoading: boolean } => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Use stable asset path and update counter for dependency array
  const assetPath = asset.path;
  const assetType = getAssetType(asset);
  const updateCounter = asset.thumbnailUpdateCounter;
  const supportsThumbnails = asset.supportsThumbnails;

  // Load thumbnail effect - MobX observer handles reactivity when updateCounter changes
  // isThumbnailServiceReady is now an observable dependency so the effect re-runs when it becomes true
  useEffect(() => {
    if (!supportsThumbnails || assetType === AssetType.Scene) {
      setThumbnailUrl(null);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    setIsLoading(true);

    const loadThumbnailUrl = async () => {
      try {
        const thumbnailService = editorStore.thumbnailService;
        if (!thumbnailService) {
          setIsLoading(false);
          return;
        }

        const thumbnailPath = thumbnailService.getThumbnailPath(asset);
        const storageProvider = editorStore.project.makeScopedStorageProvider();

        // Check if thumbnail exists using cached method
        const exists = await thumbnailService.thumbnailExists(asset);
        if (!exists) {
          // Queue thumbnail generation with high priority for visible assets
          thumbnailService.queueThumbnailGeneration(asset, 10);
          if (isCancelled) return;
          setIsLoading(false);
          return;
        }
        if (isCancelled) {
          setIsLoading(false);
          return;
        }

        // Get thumbnail URL
        const resolvedPath = await storageProvider.resolvePath(ENGINE.AssetPath.fromString(thumbnailPath));
        const baseUrl = resolvedPath.getResolvedPath();

        // Add cache-busting parameter to force browser to reload updated thumbnails
        const url = `${baseUrl}?v=${updateCounter}`;

        if (!isCancelled) {
          // Preload the image to avoid flicker
          const img = new Image();
          img.onload = () => {
            if (!isCancelled) {
              setThumbnailUrl(url);
              setIsLoading(false);
            }
          };
          img.onerror = () => {
            if (!isCancelled) {
              const svc = editorStore.thumbnailService;
              if (svc && asset.supportsThumbnails) {
                svc.invalidateThumbnailCache([asset.path]);
                asset.refreshThumbnail();
                svc.queueThumbnailGeneration(asset, 12);
              }
              setIsLoading(false);
            }
          };
          img.src = url;
        }
      } catch (error) {
        // Silently fail - will use fallback icon
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadThumbnailUrl();

    return () => {
      isCancelled = true;
    };
  }, [assetPath, assetType, updateCounter, supportsThumbnails, asset, isThumbnailServiceReady]);

  return { thumbnailUrl, isLoading };
};

/**
 * AssetThumbnail component to render the appropriate icon for an asset.
 */
export const AssetThumbnail = observer(({ asset, iconSize, selected = false }: AssetThumbnailProps) => {
  const type = getAssetType(asset);
  const IconComponent = ASSET_ICONS[type] ?? FileTextIcon;
  const isEngineAsset = !asset.isProjectItem;
  const isThumbnailServiceReady = editorStore.isThumbnailServiceReady;
  const { thumbnailUrl, isLoading } = useThumbnailUrl(asset, isThumbnailServiceReady);
  const { url: screenshotUrl, isLoading: isLoadingScreenshot } = useSceneScreenshot(
    type === AssetType.Scene ? asset.path : null,
    asset.thumbnailUpdateCounter
  );

  // Common image style for thumbnails and textures
  const imageStyle = {
    width: iconSize,
    height: iconSize,
    objectFit: 'contain' as const,
    borderRadius: 4,
  };

  // For scene assets, show screenshot with icon overlay if available
  if (type === AssetType.Scene && screenshotUrl) {
    const overlayIconSize = Math.max(10, Math.min(20, iconSize * 0.18));
    return (
      <div style={{ position: 'relative', width: iconSize, height: iconSize }}>
        <NextImage
          src={screenshotUrl}
          alt={asset.name}
          width={iconSize}
          height={iconSize}
          unoptimized
          style={{
            width: iconSize,
            height: iconSize,
            objectFit: 'cover',
            borderRadius: 4
          }}
        />
        {/* Overlay icon */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderTopLeftRadius: 4,
            borderBottomRightRadius: 4,
            padding: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MountainIcon size={overlayIconSize} strokeWidth={1.5} color="white" />
        </div>
      </div>
    );
  }

  // For supported asset types, handle thumbnail loading state
  if (asset.supportsThumbnails) {
    if (thumbnailUrl) {
      return (
        <NextImage
          src={thumbnailUrl}
          alt={asset.name}
          width={iconSize}
          height={iconSize}
          unoptimized
          style={imageStyle}
        />
      );
    }

    // If still loading, show a placeholder to avoid flicker
    if (isLoading || isLoadingScreenshot) {
      return (
        <div
          style={{
            width: iconSize,
            height: iconSize,
            borderRadius: 4,
            ...(selected && { outline: '2px solid hsl(var(--primary))' }),
          }}
        />
      );
    }
  }

  // Scene without a screenshot yet, or loading before first paint: neutral box (not full Mountain icon)
  if (type === AssetType.Scene && isLoadingScreenshot && !screenshotUrl) {
    return (
      <div
        style={{
          width: iconSize,
          height: iconSize,
          borderRadius: 4,
          ...(selected && { outline: '2px solid hsl(var(--primary))' }),
        }}
      />
    );
  }

  // For texture assets, show the texture itself if available
  if (type === AssetType.Texture && asset.url) {
    return (
      <NextImage
        src={asset.url}
        alt={asset.name}
        width={iconSize}
        height={iconSize}
        unoptimized
        style={imageStyle}
        onLoad={e => {
          (e.target as HTMLImageElement).style.opacity = '1';
        }}
        onLoadStart={e => {
          (e.target as HTMLImageElement).style.opacity = '0';
        }}
      />
    );
  }

  // Fallback to icon
  // Determine color classes
  let colorClass = '';
  if (isEngineAsset) {
    colorClass = 'text-engine-asset';
  } else if (type === AssetType.Prefab) {
    colorClass = 'text-prefab';
  } else if (selected) {
    // colorClass = 'text-primary-foreground';
  }

  // For resource assets, prefer the per-class engine icon over the generic DatabaseIcon
  if (type === AssetType.Resource && asset.resourceClass) {
    const classIcon = assetsStore.resourceClassMap.get(asset.resourceClass)?.classIcon;
    if (classIcon) {
      return <ClassIconImg iconName={classIcon} size={iconSize} className={colorClass} />;
    }
  }

  const iconProps = {
    size: iconSize,
    strokeWidth: 1,
    className: colorClass,
  };

  return <IconComponent {...iconProps} />;
});
AssetThumbnail.displayName = 'AssetThumbnail';
